// @ts-nocheck
import React from 'react';
import { useDialerUI } from '@/contexts/DialerUIContext';
import { applyMute } from '@/services/dialer/muteCall';
import { isVoicemailDropInProgress } from '@/components/dialer/call-panel/micInCall';
import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Square, Mic, MicOff, Hash, ChevronUp, ChevronDown, PhoneCall, Phone, PhoneOff, ExternalLink, SkipForward, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — JS module, Phase 4 cleanup
import { useDialer } from '@/contexts/DialerContext';
import { useNow } from '@/hooks/useNow';
import { useLiveCallContact } from '@/hooks/useLiveCallContact';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — JS module
import { dialIntent } from '@/services/dialer/dialRules';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { VFAvatar } from '@/components/vf';
import { isAudibleCall } from '@/services/dialer/stopUpdates';

const STORAGE_KEY = 'xdial_floating_bar_position';

type BarPosition = 'top' | 'bottom';

function getSavedPosition(): BarPosition {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'top' ? 'top' : 'bottom';
  } catch {
    return 'bottom';
  }
}
function savePosition(pos: BarPosition) {
  try {
    localStorage.setItem(STORAGE_KEY, pos);
  } catch {
    /* noop */
  }
}

function formatPhoneShort(phone?: string | null) {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') return '+1 …' + digits.slice(-4);
  if (digits.length >= 10) return '…' + digits.slice(-4);
  return phone;
}

// Twilio met parfois "Verified Caller ID …" / "(verified)" dans le
// friendly_name d'un numero. C'est un detail d'admin Twilio qui pollue la
// pilule "Numero emetteur" cote caller. On strip ces mentions sans toucher
// au reste du friendlyName (qui peut etre une vraie etiquette utile).
function cleanFriendlyName(name?: string | null): string | null {
  if (!name) return null;
  const cleaned = name
    .replace(/\(\s*verified\s*\)/gi, '')
    .replace(/\(\s*v[ée]rifi[ée]\s*\)/gi, '')
    .replace(/\bverified\s*caller\s*id\b/gi, '')
    .replace(/\bv[ée]rifi[ée]\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned || null;
}

function formatSessionDuration(startedAt: number | null | undefined): string {
  if (!startedAt) return '0:00';
  const s = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${(s % 60).toString().padStart(2, '0')}`;
}

export function formatCallDuration(
  startedAt: number | string | Date | null | undefined,
  currentTime: Date | number | null | undefined,
): string {
  if (!startedAt) return '0:00';
  const start = typeof startedAt === 'number' ? startedAt : new Date(startedAt).getTime();
  const now = currentTime instanceof Date ? currentTime.getTime() : currentTime || Date.now();
  const elapsed = Math.max(0, Math.floor((now - start) / 1000));
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/** DES-10: no session below this width (the call page applies the same rule). */
export const MIN_SESSION_WIDTH = 1024;

function useViewportWidth(): number {
  const [w, setW] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  React.useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
}

interface ProspectShape {
  status?: string | null;
  prospect_phone?: string | null;
  source?: string | null;
  customer_id?: string | null;
  last_call_status?: string | null;
  do_not_call?: boolean | null;
}
interface CallShape {
  call_status?: string | null;
}
interface TwilioNumber {
  phoneNumber: string;
  friendlyName?: string | null;
}

interface ConnectedContactShape {
  prospect_name?: string | null;
  prospect_company?: string | null;
  prospect_phone?: string | null;
  name?: string | null;
  company?: string | null;
  phone?: string | null;
  callStartedAt?: number | string | Date | null;
}

interface DialerCtx {
  dialerRunState?: 'running' | 'paused' | 'idle' | null;
  stoppingAll?: boolean;
  isMuted: boolean;
  setIsMuted: (m: boolean) => void;
  handleStartDialing: () => void;
  handleManualStartDialing?: () => void;
  handleStopAll: () => void;
  handleStopDialing?: () => void;
  handleHangupAndContinue?: () => Promise<void> | void;
  handleHangupAndNext?: () => Promise<void> | void;
  powerMode?: unknown;
  setPowerMode?: (v: unknown) => void;
  parallelCallsCount: number;
  setParallelCallsCount: (n: number) => void;
  selectedCallerId?: string | null;
  setSelectedCallerId: (id: string) => void;
  twilioNumbers?: TwilioNumber[];
  activeCallerNumbers?: TwilioNumber[];
  setShowKeypad?: (open: boolean) => void;
  setSelectedProspect?: (p: unknown) => void;
  prospectQueue?: ProspectShape[];
  activeCalls?: CallShape[];
  language?: string;
  connectedContact?: ConnectedContactShape | null;
  currentTime?: Date | number | null;
  dialingSessionStartedAt?: number | null;
  selectedSource?: string | null;
  selectedCustomerId?: string | null;
  latestCallByPhone?: Record<string, { call_outcome?: string | null } | undefined>;
  dialedPhonesThisSession?: string[];
}

interface TwilioCallLite {
  mute(state: boolean): void;
}
interface TwilioDeviceLike {
  calls?: { size?: number; forEach: (cb: (c: TwilioCallLite) => void) => void };
}

interface FloatingCallBarProps {
  selectedView: string;
  /** call_page (W3): « Lancer » opens the call page, which reconciles then starts. */
  onStart?: () => void;
}

export default function FloatingCallBar({ selectedView, onStart }: FloatingCallBarProps) {
  const {
    dialerRunState,
    stoppingAll,
    isMuted,
    setIsMuted,
    handleStartDialing,
    handleManualStartDialing,
    handleStopAll,
    handleStopDialing,
    handleHangupAndContinue,
    handleHangupAndNext,
    powerMode,
    parallelCallsCount,
    setParallelCallsCount,
    selectedCallerId,
    setSelectedCallerId,
    twilioNumbers = [],
    activeCallerNumbers = [],
    setShowKeypad,
    setSelectedProspect,
    prospectQueue = [],
    activeCalls = [],
    connectedContact: ctxConnectedContact,
    dialingSessionStartedAt,
    selectedSource,
    selectedCustomerId,
    latestCallByPhone = {},
    dialedPhonesThisSession = [],
  } = useDialer() as DialerCtx;
  // The list the contacts screen shows: Power Mode dials only inside it.
  const dialScopeIds = (useDialer() as { dialScopeIds?: Set<string> | null }).dialScopeIds ?? null;
  // Read from DialerUIContext: useDialer() never exposed these three, so the
  // "Rouvrir" branches of this bar could not show (RUI-P2).
  const { lastProspectContacted, reopenLastProspect, clearLastProspectContacted } = useDialerUI();
  // The call duration ticks on its own clock during a call (2026-10-07), and
  // the session timer while the session runs (RUI-08: it froze between calls).
  const currentTime = useNow(1000, !!ctxConnectedContact || dialerRunState === 'running');

  const { t } = useTranslation(['dialer', 'common']);
  const viewportWidth = useViewportWidth();
  const [position, setPosition] = useState<BarPosition>(getSavedPosition);

  // Qui est en ligne : réponse partagée avec la pastille du rail (DES-2),
  // même priorité qu'avant (contexte, machine d'appel, entrant décroché).
  const { connectedContact: liveContact, isInboundLive, inbound, callEnded } = useLiveCallContact();
  const connectedContact = liveContact as ConnectedContactShape | null;

  // During an automatic voicemail drop the server plays the recorded message:
  // the agent's microphone is out of the call, so the mute button is hidden,
  // as ProspectDetailPanel hides its controls when pillState is 'voicemail'.
  const micOutOfCall = isVoicemailDropInProgress(
    activeCalls as Array<{ id?: string; call_status?: string | null }>,
    (connectedContact as { callId?: string | null } | null)?.callId ?? null,
  );

  /** Ouvre la fiche du contact en ligne, reconstruite depuis l'appel. */
  const ouvrirLaFiche = useCallback(() => {
    const cc = connectedContact as Record<string, unknown> | null;
    if (!cc) return;
    setSelectedProspect?.({
      id: cc.prospect_id || '',
      prospect_name: cc.prospect_name || cc.name || '',
      prospect_phone: cc.prospect_phone || cc.phone || '',
      prospect_company: cc.prospect_company || cc.company || '',
      prospect_title: cc.prospect_title || '',
      prospect_email: cc.prospect_email || cc.email || '',
      linkedin_url: cc.linkedin_url || '',
      website: cc.website || '',
    });
  }, [connectedContact, setSelectedProspect]);

  const handleMute = useCallback(() => {
    const next = !isMuted;
    // La logique vit dans services/dialer/muteCall : la fiche contact a le
    // meme bouton, et deux copies auraient diverge.
    if (!applyMute(next)) {
      toast.error(t('dialer:callBar.muteUnavailable', { defaultValue: 'Aucun appel actif à couper' }));
      return;
    }
    setIsMuted(next);
    toast.info(next ? t('dialer:callBar.mute') : t('dialer:callBar.unmute'));
  }, [isMuted, setIsMuted, t]);

  const handleStartStop = useCallback(() => {
    // eslint-disable-next-line no-console
    console.log('[DIAL] FloatingCallBar click', {
      dialerRunState,
      prospectQueueSize: prospectQueue.length,
      pendingCount: prospectQueue.filter((p) => p.status === 'active' || p.status === 'pending').length,
      activeCallsSize: activeCalls.length,
      stoppingAll,
      selectedCallerId,
      parallelCallsCount,
      hasHandleManual: typeof handleManualStartDialing === 'function',
      hasHandleStart: typeof handleStartDialing === 'function',
    });
    if (dialerRunState === 'running') {
      // ENG-1: with a human on the line, "Arrêter" stops dialing but keeps
      // the conversation; ending it is "Raccrocher".
      if (handleStopDialing && activeCalls.some(isAudibleCall)) {
        // eslint-disable-next-line no-console
        console.log('[DIAL] → handleStopDialing');
        handleStopDialing();
      } else {
        // eslint-disable-next-line no-console
        console.log('[DIAL] → handleStopAll');
        handleStopAll();
      }
    } else {
      // eslint-disable-next-line no-console
      console.log('[DIAL] → handleManualStartDialing');
      if (onStart) onStart();
      else (handleManualStartDialing || handleStartDialing)();
    }
  }, [dialerRunState, handleStopAll, handleStopDialing, handleStartDialing, handleManualStartDialing, onStart, prospectQueue, activeCalls, stoppingAll, selectedCallerId, parallelCallsCount]);

  const handleTogglePos = useCallback(() => {
    setPosition((p) => {
      const n: BarPosition = p === 'bottom' ? 'top' : 'bottom';
      savePosition(n);
      return n;
    });
  }, []);

  const hasActiveCalls = activeCalls && activeCalls.length > 0;
  const isRunning = dialerRunState === 'running';
  const isLiveCall = !!connectedContact;

  // 2026-05-21 — "Annuler" vs "Arreter" : tant que les appels sont en train
  // de composer/sonner (personne n'a decroche), le bouton "Annuler les appels"
  // signale qu'on abandonne une tentative en cours. Des qu'au moins un appel
  // a un humain en ligne, OU qu'aucun appel n'est actif, on revient a
  // "Arreter les appels" (on coupe une session, pas une simple sonnerie).
  const RINGING_STATUSES = ['dialing', 'ringing', 'ai_analyzing'];
  const someoneAnswered = (activeCalls ?? []).some(
    (c) => c.call_status === 'connected_to_agent' || c.call_status === 'human_detected',
  );
  const onlyRinging =
    hasActiveCalls &&
    !someoneAnswered &&
    (activeCalls ?? []).every((c) => RINGING_STATUSES.includes(c.call_status ?? ''));
  const stopLabel = onlyRinging
    ? t('dialer:callBar.cancel', { defaultValue: 'Annuler' })
    : t('dialer:callBar.stop');

  // Visibility :
  //   - branch "live call" si connectedContact !== null (peu importe la vue)
  //   - sinon branch "dial control" si vue queue OU dialer en marche / appels actifs
  //   - sinon branch "reopen last" si lastProspectContacted (apres qualif/raccrochage)
  const hasLastContact = !isLiveCall && !!lastProspectContacted;
  if (!isLiveCall && !hasLastContact && selectedView !== 'queue' && !isRunning && !hasActiveCalls) return null;

  // ── Branch "reopen last" — apres qualif/raccrochage, permet de rouvrir la modale ──
  // 2026-06-05 (demande Kevin) : quand la modale a ete fermee mais qu'on a un
  // dernier prospet contacte, on affiche un mini-bandeau "Rouvrir [Nom]" pour
  // pouvoir revenir sur la fiche sans avoir a re-naviguer dans la liste.
  // Branch prioritaire seulement si aucune autre raison de montrer la barre.
  if (hasLastContact && !isRunning && !hasActiveCalls && selectedView !== 'queue') {
    const lp = lastProspectContacted as Record<string, unknown>;
    const lpName = String(lp.prospect_name || lp.name || 'Dernier prospect');
    const lpCompany = String(lp.prospect_company || lp.company || '');
    // `position` vaut 'top' ou 'bottom', comme partout ailleurs dans ce
    // fichier. Cette branche lisait `position.x` / `position.y` et posait un
    // `onPointerDown` qui n'existe pas : restes d'une barre qu'on deplacait a
    // la souris. Le bandeau se place donc comme les deux autres branches.
    return (
      <div
        className="fixed z-50 left-1/2 -translate-x-1/2"
        style={position === 'top' ? { top: 52 } : { bottom: 16 }}
      >
        <div
          className="flex items-center gap-3 rounded-vf shadow-vf-lg px-4 h-12"
          style={{ background: 'var(--vf-bg-elevated, white)', border: '1px solid var(--vf-border, rgba(0,0,0,0.08))' }}
        >
          <span className="vf-text-1 font-medium text-sm">{lpName}</span>
          {lpCompany && <span className="vf-text-3 text-xs">· {lpCompany}</span>}
          <button
            onClick={() => reopenLastProspect?.()}
            className="inline-flex items-center gap-1.5 rounded-vf px-3 h-8 text-xs font-medium bg-vf-primary text-white hover:opacity-90 transition-opacity"
            style={{ background: 'var(--vf-primary, #111827)', color: 'white' }}
          >
            <ExternalLink size={12} />
            {t('dialer:floatingCallBar.reopenSheet')}
          </button>
          <button
            onClick={() => clearLastProspectContacted?.()}
            title={t('dialer:floatingCallBar.closeReminder')}
            className="inline-flex items-center justify-center rounded-vf w-7 h-7 vf-text-3 hover:vf-text-1"
            style={{ background: 'transparent' }}
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  // ── Branch "live call" — ex-OnCallBar mergé ici ────────────────────────────
  // Quand un appel est connecté on remplace toute la barre par un layout
  // dédié (avatar + nom + société/phone + durée + "Ouvrir détails" + Mute +
  // Raccrocher). Le start/stop dialer + parallel + caller-id n'a aucun sens
  // pendant un appel one-to-one en cours.
  if (isLiveCall && connectedContact) {
    // 2026-05-15 — fallback "Chargement..." au lieu de "Contact" pour signaler
    // que c'est temporaire (enrichissement en cours).
    const liveName = connectedContact.prospect_name || connectedContact.name || 'Chargement…';
    const liveCompany = connectedContact.prospect_company || connectedContact.company || '';
    const livePhone = connectedContact.prospect_phone || connectedContact.phone || '';
    const livePosStyle: React.CSSProperties =
      position === 'top' ? { top: 44, bottom: 'auto' } : { bottom: 0, top: 'auto' };
    return (
      <motion.div
        key={`fbar-live-${position}`}
        initial={{ opacity: 0, y: position === 'top' ? -12 : 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        style={{
          // QA-W4-3: positioned in the content column (DialerLayout), not
          // over the whole screen: the sidebar's bottom stays clickable.
          position: 'absolute',
          left: 0,
          right: 0,
          zIndex: 3500,
          ...livePosStyle,
          // #8 — alignee sur la barre principale : meme hauteur, meme ombre.
          height: 48,
          background: 'var(--vf-green)',
          color: 'white',
          boxShadow: '0 -1px 4px rgba(0,0,0,0.10)',
        }}
        // Kevin, 2026-09-30 : toute la ligne ouvre la fiche, au lieu d'un
        // bouton « Ouvrir les details » a cote des actions. Les boutons a
        // droite gardent leur geste : ils arretent la propagation.
        className="flex items-center gap-4 px-4 cursor-pointer"
        role="button"
        tabIndex={0}
        title={t('dialer:onCall.openDetails')}
        onClick={ouvrirLaFiche}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ouvrirLaFiche(); }
        }}
      >
        <VFAvatar name={liveName} size={36} className="ring-2 ring-white/20" />

        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-semibold">{liveName}</div>
          {/* #8 -- « numero tronque ». Cause : cette ligne portait `truncate` et
              contenait entreprise + separateur + numero dans un seul flux. Une
              raison sociale un peu longue mangeait la largeur et c'est LE NUMERO
              qui disparaissait — la donnee la plus utile pendant un appel.
              Desormais l'entreprise rétrécit (`truncate`), le numero jamais
              (`shrink-0`) : la troncature devient structurellement impossible
              sur le numero, quelle que soit la largeur de la fenetre. */}
          <div className="flex items-center gap-1 min-w-0 text-[12px] opacity-90">
            {liveCompany && <span className="truncate">{liveCompany}</span>}
            {liveCompany && livePhone ? <span className="shrink-0">·</span> : null}
            {livePhone && (
              <span className="shrink-0 vf-font-mono tabular-nums" data-testid="call-bar-phone">
                {livePhone}
              </span>
            )}
          </div>
        </div>

        <span className="vf-font-mono font-semibold tabular-nums" style={{ fontSize: 22 }}>
          {formatCallDuration(connectedContact.callStartedAt, currentTime)}
        </span>

        {/* Les actions gardent leur geste : sans cette garde, chaque clic
            sur Couper le micro ou Raccrocher ouvrirait aussi la fiche. */}
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {!micOutOfCall && (
          <button
            onClick={handleMute}
            title={isMuted ? t('dialer:callBar.unmute') : t('dialer:callBar.mute')}
            className="inline-flex items-center justify-center rounded-vf"
            style={{
              width: 36,
              height: 36,
              background: isMuted ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)',
              color: 'white',
            }}
          >
            {isMuted ? <MicOff size={15} /> : <Mic size={15} />}
          </button>
          )}
          <button
            onClick={() => setShowKeypad?.(true)}
            title={t('dialer:callBar.keypad')}
            className="inline-flex items-center justify-center rounded-vf"
            style={{
              width: 36,
              height: 36,
              background: 'rgba(255,255,255,0.15)',
              color: 'white',
            }}
          >
            <Hash size={15} />
          </button>
          {/* Nothing to hang up once the call ended and waits for its outcome
              (CALL-PILL-3): « Raccrocher » would close the sheet to qualify. */}
          {!callEnded && (
          <button
            onClick={() => { if (isInboundLive) void inbound.handleHangup(); else handleStopAll(); }}
            title={t('dialer:onCall.hangUp')}
            className="inline-flex items-center gap-1.5 rounded-vf px-3 h-9 text-[12px] font-semibold text-white"
            style={{ background: 'var(--vf-red)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.filter = 'brightness(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.filter = 'brightness(1)';
            }}
          >
            <PhoneOff size={13} />
            {t('dialer:onCall.hangUp')}
          </button>
          )}
          {/* "Raccrocher et continuer" — visible en Power Mode : raccroche
              l'appel en cours et relance immediatement le batch suivant.
              Demande Kevin 2026-05-29 (acces en deux endroits : carte +
              bandeau bas) pour eviter de devoir ouvrir la CallSheet pour
              passer au suivant en mode high-volume. */}
          {/* "Raccrocher et passer au suivant" = clic explicite qui RELANCE
              le batch immediatement (handleHangupAndNext). Avant ce bouton
              etait cable sur handleHangupAndContinue qui passe juste en
              pause sans relancer — l'agent voyait alors "Redial la liste"
              au lieu de l'enchainement attendu. Audit Kevin 2026-06-02. */}
          {/* Not during an inbound call shown over a pending qualification
              (CALL-PILL-2), nor once the outbound call ended and waits for
              its outcome (CALL-PILL-3): « suivant » would skip it. */}
          {powerMode && !isInboundLive && !callEnded && (handleHangupAndNext || handleHangupAndContinue) && (
            <button
              onClick={() => { void (handleHangupAndNext || handleHangupAndContinue)?.(); }}
              title={t('dialer:onCall.hangUpNext', { defaultValue: 'Raccrocher et passer au suivant' })}
              className="inline-flex items-center gap-1.5 rounded-vf px-3 h-9 text-[12px] font-semibold"
              style={{ background: 'rgba(255,255,255,0.92)', color: 'var(--vf-green)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.filter = 'brightness(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.filter = 'brightness(1)';
              }}
            >
              <SkipForward size={13} />
              {t('dialer:onCall.hangUpNext', { defaultValue: 'Raccrocher et passer au suivant' })}
            </button>
          )}
        </div>
      </motion.div>
    );
  }

  // Scope counts to the active list/customer the user is viewing — sinon
  // l'utilisateur voit "440 dialable(s)" alors qu'il regarde une liste de 5
  // prospects. Convention identique au handleStartDialing dans DialerContext.
  const matchesActiveScope = (p: ProspectShape) => {
    if (selectedCustomerId && (p.customer_id || '') !== selectedCustomerId) return false;
    if (!selectedSource) return true;
    return selectedSource === 'Revcall Client Dashboard'
      ? !p.source || p.source === selectedSource
      : p.source === selectedSource;
  };
  const scopedQueue = prospectQueue.filter(matchesActiveScope);
  // ADR-031 + 2026-05-13 — alignement strict avec canCall() cote DialerContext :
  // exclut les archives (RDV pris / pas interesse / DNC), les rappels demandes
  // (vue dediee) ET les deja contactes (sortis du Power Mode). Le compteur
  // affiche donc exactement le nombre d'appels prevus restants cette session.
  const dialedSessionSet = new Set(dialedPhonesThisSession);
  const isDialableInQueue = (p: ProspectShape) => {
    if (!p.prospect_phone || p.prospect_phone.trim() === '') return false;
    if (p.status === 'placeholder') return false;
    if (dialedSessionSet.has(p.prospect_phone)) return false;
    if (dialScopeIds && !dialScopeIds.has((p as { id?: string }).id ?? '')) return false;
    const intent = dialIntent(p, latestCallByPhone[p.prospect_phone]);
    return intent === 'fresh' || intent === 'no_answer';
  };
  const dialableProspects = scopedQueue.filter(isDialableInQueue);
  const pendingCount = dialableProspects.length;
  const dialableCount = pendingCount;
  const hasTenantNumber = twilioNumbers.length > 0;
  const hasActiveNumber = activeCallerNumbers.length > 0;
  // DES-10 (QA-W4-7): below 1024 px the list says why and does not start a
  // session. A running session can still be stopped. Under call_page
  // (`onStart` set), « Lancer » opens the call page, which says it itself.
  const tooNarrow = !onStart && viewportWidth < MIN_SESSION_WIDTH;
  const canStart = hasActiveNumber && dialableCount > 0 && !stoppingAll && !tooNarrow;

  // Bascule "Redial la liste" : si plus de 95% des dialables ont déjà été
  // travaillés (intent != 'fresh'), on relabelle pour signaler qu'on
  // n'attaque plus une liste fraîche mais qu'on repasse.
  const isRedialMode = dialableCount > 0 && (() => {
    let nonFresh = 0;
    for (const p of dialableProspects) {
      const intent = dialIntent(p, latestCallByPhone[p.prospect_phone || '']);
      if (intent !== 'fresh') nonFresh++;
    }
    return nonFresh / dialableCount >= 0.95;
  })();

  const startTooltip = tooNarrow
    ? t('dialer:callPage.session.narrow')
    : stoppingAll
    ? 'Arrêt en cours…'
    : !hasTenantNumber
      ? "Aucun numéro Twilio provisionné. Allez dans Paramètres > Numéros pour en obtenir un avant de pouvoir lancer."
      : !hasActiveNumber
        ? "Tous vos numéros sont désactivés. Réactivez-en au moins un dans Paramètres > Numéros."
        : pendingCount === 0
          ? 'Aucun prospect en attente — importez ou ajoutez des prospects'
          : dialableCount === 0
            ? "Aucun prospect dialable : les prospects en attente n'ont pas de numéro de téléphone valide."
            : isRedialMode
              ? `Redial ${dialableCount} prospect(s) déjà travaillé(s) — ${Math.min(parallelCallsCount, dialableCount)} en parallèle`
              : `Lancer ${Math.min(parallelCallsCount, dialableCount)} appel(s) parallèle(s) — ${dialableCount} dialable(s)`;

  const posStyle: React.CSSProperties =
    position === 'top' ? { top: 44, bottom: 'auto' } : { bottom: 0, top: 'auto' };

  const bgColor = isRunning ? 'var(--vf-orange)' : 'var(--vf-surface-1)';
  const textColor = isRunning ? 'white' : 'var(--vf-text-1)';

  return (
    <motion.div
      key={`fbar-${position}-${isRunning}`}
      initial={{ opacity: 0, y: position === 'top' ? -12 : 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      style={{
        // QA-W4-3: in the content column, beside the sidebar, not over it.
        position: 'absolute',
        left: 0,
        right: 0,
        zIndex: 3500,
        ...posStyle,
        background: bgColor,
        borderTop: position === 'bottom' ? '1px solid var(--vf-border)' : 'none',
        borderBottom: position === 'top' ? '1px solid var(--vf-border)' : 'none',
        color: textColor,
        // #8 — une seule geometrie d'ombre dans tous les etats : seule la
        // teinte change en Power Mode. Trois ombres differentes coexistaient
        // sur le meme element (20px/.15, 24px/.35, 24px/.25) — c'est
        // l'incoherence signalee. Allegee au passage.
        boxShadow: isRunning ? '0 -1px 4px rgba(249,115,22,0.22)' : '0 -1px 4px rgba(0,0,0,0.10)',
      }}
      /* #8 — barre allegee : 56 px (h-14) -> 48 px (h-12). */
      className="flex items-center gap-2 sm:gap-3 h-12 px-2 sm:px-4"
    >
      <div className="flex flex-1 items-center gap-2 min-w-0">
        {isRunning ? (
          <>
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="absolute inline-flex h-full w-full rounded-full bg-white/60 vf-pulse" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
            </span>
            <span className="text-[13px] font-semibold">
              {/* Cap a parallelCallsCount : pendant les transitions de batch
                  (un batch pas encore nettoye en DB + le refill deja parti),
                  activeCalls.length peut depasser le nombre de lignes
                  configurees (ex: 5 alors que config=3). On affiche jamais
                  plus que la config pour ne pas troubler l'utilisateur. */}
              {t('dialer:callBar.dialing', { count: Math.min(activeCalls?.length ?? 0, parallelCallsCount || 1) })}
            </span>
            <span className="text-[12px] opacity-80">
              {t('dialer:callBar.pending', { count: pendingCount })}
            </span>
          </>
        ) : (
          <>
            <span
              className="h-2 w-2 rounded-full shrink-0"
              style={{ background: canStart ? 'var(--vf-green)' : 'var(--vf-text-4)' }}
            />
            <span className="min-w-0 truncate text-[13px] font-semibold" style={{ color: 'var(--vf-text-1)' }} data-testid="call-bar-state">
              {tooNarrow
                ? t('dialer:callPage.session.narrow')
                : !hasTenantNumber
                ? t('dialer:floatingCallBar.noNumberProvisioned')
                : canStart
                  ? t('dialer:callBar.readyToCall')
                  : pendingCount === 0
                    ? t('dialer:callBar.noProspectsPending')
                    : t('dialer:floatingCallBar.noValidPhone')}
            </span>
            {canStart && (
              <span className="hidden sm:inline whitespace-nowrap text-[12px]" style={{ color: 'var(--vf-text-3)' }}>
                · {t('dialer:floatingCallBar.dialable', { count: dialableCount })}
              </span>
            )}
          </>
        )}
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
        <Select
          value={String(parallelCallsCount)}
          onValueChange={(v) => setParallelCallsCount(Number(v))}
        >
          <SelectTrigger
            className="hidden sm:flex h-9 w-[110px] text-[12px] gap-1.5"
            style={
              isRunning
                ? { background: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.3)', color: 'white' }
                : { background: 'var(--vf-surface-2)', borderColor: 'var(--vf-border)', color: 'var(--vf-text-1)' }
            }
            title={t('dialer:callBar.parallelLines')}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="end" className="min-w-[140px]">
            {[1, 2, 3, 4, 5].map((n) => (
              <SelectItem key={n} value={String(n)} className="text-[13px]">
                <span className="font-medium tabular-nums">{n}</span>
                <span className="ml-1.5 text-muted-foreground">
                  {t('dialer:callBar.line', { count: n })}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {activeCallerNumbers.length > 0 && (
          <Select
            value={selectedCallerId || activeCallerNumbers[0]?.phoneNumber || ''}
            onValueChange={(v) => setSelectedCallerId(v)}
          >
            <SelectTrigger
              className="hidden sm:flex h-9 w-[170px] text-[12px] gap-1.5"
              style={{
                background: isRunning ? 'rgba(255,255,255,0.18)' : 'var(--vf-surface-2)',
                borderColor: isRunning ? 'rgba(255,255,255,0.3)' : 'var(--vf-border)',
                color: isRunning ? 'white' : 'var(--vf-text-1)',
              }}
              title={t('dialer:callBar.callerId')}
            >
              <Phone size={12} className="shrink-0 opacity-70" />
              <SelectValue placeholder={t('dialer:floatingCallBar.callerNumber')} />
            </SelectTrigger>
            <SelectContent align="end" className="min-w-[260px]">
              <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {t('dialer:floatingCallBar.callerNumber')}
              </div>
              {activeCallerNumbers.map((n) => {
                const cleanName = cleanFriendlyName(n.friendlyName);
                return (
                  <SelectItem key={n.phoneNumber} value={n.phoneNumber} className="py-2">
                    <span className="font-medium tabular-nums text-[13px]">
                      {cleanName || formatPhoneShort(n.phoneNumber)}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}

        {!micOutOfCall && (
        <button
          onClick={handleMute}
          title={isMuted ? t('dialer:callBar.unmute') : t('dialer:callBar.mute')}
          className="flex items-center justify-center rounded-vf transition-colors"
          style={{
            width: 36,
            height: 36,
            background: isMuted
              ? isRunning
                ? 'rgba(255,255,255,0.3)'
                : 'var(--vf-red-bg)'
              : isRunning
                ? 'rgba(255,255,255,0.15)'
                : 'var(--vf-surface-2)',
            color: isMuted ? (isRunning ? 'white' : 'var(--vf-red)') : isRunning ? 'white' : 'var(--vf-text-2)',
            border: isRunning ? 'none' : '1px solid var(--vf-border)',
          }}
        >
          {isMuted ? <MicOff size={14} /> : <Mic size={14} />}
        </button>
        )}

        <button
          onClick={() => setShowKeypad?.(true)}
          title={t('dialer:callBar.keypad')}
          className="flex items-center justify-center rounded-vf"
          style={{
            width: 36,
            height: 36,
            background: isRunning ? 'rgba(255,255,255,0.15)' : 'var(--vf-surface-2)',
            color: isRunning ? 'white' : 'var(--vf-text-2)',
            border: isRunning ? 'none' : '1px solid var(--vf-border)',
          }}
        >
          <Hash size={14} />
        </button>

        {isRunning && dialingSessionStartedAt && (
          <span
            className="vf-font-mono tabular-nums font-semibold"
            style={{ fontSize: 18, minWidth: 56, textAlign: 'right' }}
          >
            {formatSessionDuration(dialingSessionStartedAt)}
          </span>
        )}

        {/* RUI-P2 : sur la vue file, « Rouvrir » reste accessible apres un
            raccroche pour qualifier le dernier appel. */}
        {hasLastContact && !isRunning && !hasActiveCalls && (
          <button
            type="button"
            onClick={() => reopenLastProspect?.()}
            className="inline-flex items-center gap-1.5 rounded-vf px-3 h-9 text-xs font-medium vf-text-1"
            style={{ border: '1px solid var(--vf-border, rgba(0,0,0,0.12))', background: 'transparent' }}
          >
            <ExternalLink size={12} />
            {t('dialer:floatingCallBar.reopenSheet')}
          </button>
        )}

        <button
          onClick={handleStartStop}
          disabled={!isRunning && !canStart}
          aria-disabled={!isRunning && !canStart}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-vf px-3 sm:px-4 h-9 text-[13px] font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            background: isRunning ? 'white' : 'var(--vf-green)',
            color: isRunning ? 'var(--vf-orange)' : 'white',
            boxShadow: 'var(--vf-shadow-sm)',
          }}
          title={isRunning ? stopLabel : startTooltip}
        >
          {isRunning ? (
            <>
              <Square size={12} fill="currentColor" /> {stopLabel}
            </>
          ) : isRedialMode ? (
            <>
              {/* Correction #77 — "Redial la liste" ne disait pas combien
                  de contacts restaient a rappeler. Le nombre reel remplace
                  le mot "liste" : on sait ce qu on relance avant de cliquer. */}
              <PhoneCall size={13} /> {t('dialer:floatingCallBar.redialContacts', { count: dialableCount })}
            </>
          ) : (
            <>
              <PhoneCall size={13} /> {t('dialer:callBar.start')}
            </>
          )}
        </button>

        <button
          onClick={handleTogglePos}
          title={position === 'bottom' ? t('dialer:callBar.moveToTop') : t('dialer:callBar.moveToBottom')}
          className="hidden sm:flex items-center justify-center rounded-vf"
          style={{
            width: 28,
            height: 28,
            background: 'transparent',
            color: isRunning ? 'rgba(255,255,255,0.75)' : 'var(--vf-text-3)',
            border: isRunning ? '1px solid rgba(255,255,255,0.2)' : '1px solid var(--vf-border)',
          }}
        >
          {position === 'bottom' ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>
    </motion.div>
  );
}
